import React, { useState, useEffect } from 'react';
import { db, auth, default as app } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from "firebase/functions";
import { Container, Card, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';

function ContactForm() {
  const [userInfo, setUserInfo] = useState({ name: '', email: '' });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const currentUser = auth.currentUser;

  useEffect(() => {
    document.title = 'お問い合わせ | My Order System';
    if (currentUser) {
      if (currentUser.isAnonymous) {
        // ゲストユーザーの場合は入力を受け付けるため、初期値は空のままにしておく
        // 固定値のセットは行わない
        setLoading(false);
      } else {
        // 会員ユーザーの場合はDBから情報を取得してセットする
        const userDocRef = doc(db, 'users', currentUser.uid);
        getDoc(userDocRef).then(docSnap => {
          if (docSnap.exists()) {
            const userData = docSnap.data();
            setUserInfo({ name: userData.personName, email: userData.email });
          } else {
            setError('ユーザー情報が見つかりません。');
          }
          setLoading(false);
        }).catch(() => {
          setError('ユーザー情報の取得に失敗しました。');
          setLoading(false);
        });
      }
    } else {
      setError('ログインしていません。');
      setLoading(false);
    }
  }, [currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // バリデーション追加
    if (!userInfo.name.trim()) {
      setError('お名前を入力してください。');
      return;
    }
    if (!userInfo.email.trim()) {
      setError('メールアドレスを入力してください。');
      return;
    }
    if (!message.trim()) {
      setError('お問い合わせ内容を入力してください。');
      return;
    }

    setSending(true);
    setError('');
    setSuccess('');

    try {
      const functions = getFunctions(app, 'asia-northeast1');
      const sendContactEmail = httpsCallable(functions, 'sendContactEmail');
      await sendContactEmail({
        name: userInfo.name,
        email: userInfo.email,
        content: message
      });
      setSuccess('お問い合わせを送信しました。管理者からの返信をお待ちください。');
      setMessage('');
      // ゲストユーザーの場合でも、連続送信を考慮して名前等はクリアしない方が親切な場合が多いですが、
      // 必要であればここで userInfo も初期化してください。
    } catch (err) {
      setError('送信に失敗しました。時間をおいて再度お試しください。');
      console.error("Inquiry submission error:", err);
    }
    setSending(false);
  };

  // ユーザー情報の入力変更ハンドラ
  const handleUserInfoChange = (e) => {
    const { name, value } = e.target;
    setUserInfo(prev => ({ ...prev, [name]: value }));
  };

  if (loading) {
    return <Container className="d-flex justify-content-center align-items-center vh-100">
      <Spinner animation="border" />
    </Container>;
  }

  // 匿名ユーザーかどうかを判定
  const isAnonymous = currentUser?.isAnonymous;

  return (
    <main>
      <Container className="my-4">
        <Card className="shadow-sm">
          <Card.Header className="p-3 d-flex flex-column flex-md-row justify-content-md-between align-items-md-end">
            <h2 className="mb-2 mb-md-0">
              お問い合わせ / ヘルプ
            </h2>
            <Link to="/mainmenu">
              <small>
                メインメニューに戻る
              </small>
            </Link>
          </Card.Header>
          <Card.Body className="p-4">
            {
              success &&
              <Alert variant="success">
                {success}
              </Alert>
            }
            {
              error &&
              <Alert variant="danger">
                {error}
              </Alert>
            }
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>
                  お名前
                </Form.Label>
                <Form.Control
                  type="text"
                  name="name" // name属性を追加
                  value={userInfo.name}
                  onChange={handleUserInfoChange} // 変更ハンドラを追加
                  // 匿名ユーザー以外は編集不可
                  readOnly={!isAnonymous}
                  disabled={!isAnonymous}
                  placeholder={isAnonymous ? "お名前を入力してください" : ""}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>
                  返信先メールアドレス
                </Form.Label>
                <Form.Control
                  type="email"
                  name="email" // name属性を追加
                  value={userInfo.email}
                  onChange={handleUserInfoChange} // 変更ハンドラを追加
                  // 匿名ユーザー以外は編集不可
                  readOnly={!isAnonymous}
                  disabled={!isAnonymous}
                  placeholder={isAnonymous ? "メールアドレスを入力してください" : ""}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>
                  お問い合わせ内容
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={8}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="システムに関するご質問やご要望などをご記入ください。"
                  required
                />
              </Form.Group>
              <div className="d-grid">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={sending}
                >
                  {
                    sending ?
                      <Spinner as="span" size="sm" />
                      :
                      '送信する'
                  }
                </Button>
              </div>
            </Form>
            <hr className="my-4" />
            <Link to="/mainmenu">
              メインメニューに戻る
            </Link>
          </Card.Body>
        </Card>
      </Container>
    </main>
  );
}

export default ContactForm;